/**
 * Manager rebuttal review deadline (CR-2259 item 12, CR-3432).
 *
 * The deadline is a number of business days counted from the day the challenge was created; weekends
 * are skipped. Holidays are not excluded yet (open question on CR-3432).
 */

/** Used when the project has no Set Manager Rebuttal Review Deadline value (CR-2259 item 12 default). */
export const DEFAULT_REVIEW_DEADLINE_DAYS = 5;

export enum ReviewDeadlineState {
  ON_TRACK = "On track",
  DUE_SOON = "Due soon",
  DUE_TODAY = "Due today",
}

/** "Due soon" covers this many business days or fewer, but more than zero. */
const DUE_SOON_MAX_DAYS = 2;

const isWeekend = (date: Date): boolean => date.getDay() === 0 || date.getDay() === 6;

const startOfDay = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const addDays = (date: Date, days: number): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/**
 * Business days that have passed since the challenge was created. A challenge created on a weekend
 * starts counting from the following Monday, so the creation day itself never uses up a day.
 */
export const businessDaysElapsed = (createdDate: Date, today: Date): number => {
  let day = startOfDay(createdDate);
  while (isWeekend(day)) {
    day = addDays(day, 1);
  }

  const end = startOfDay(today);
  let elapsed = 0;
  for (day = addDays(day, 1); day <= end; day = addDays(day, 1)) {
    if (!isWeekend(day)) {
      elapsed++;
    }
  }
  return elapsed;
};

/** Business days the manager has left. Never negative: an overdue challenge is due today until auto-resolution runs. */
export const businessDaysLeft = (createdDate: Date, deadlineDays: number, today: Date): number =>
  Math.max(deadlineDays - businessDaysElapsed(createdDate, today), 0);

export const getReviewDeadlineState = (daysLeft: number): ReviewDeadlineState => {
  if (daysLeft <= 0) {
    return ReviewDeadlineState.DUE_TODAY;
  }
  return daysLeft <= DUE_SOON_MAX_DAYS ? ReviewDeadlineState.DUE_SOON : ReviewDeadlineState.ON_TRACK;
};
